import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { createPortal } from 'react-dom';
import { useState, useEffect, useCallback, forwardRef, useImperativeHandle, useRef, useReducer } from 'react';
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  FileText,
  ClipboardList,
  Search
} from 'lucide-react';

// Convert TipTap content to simple markdown-like text
function contentToMarkdown(json) {
  if (!json || !json.content) return '';

  const processNode = (node) => {
    if (!node) return '';

    switch (node.type) {
      case 'text':
        let text = node.text || '';
        if (node.marks) {
          node.marks.forEach(mark => {
            if (mark.type === 'bold') text = `**${text}**`;
            if (mark.type === 'italic') text = `*${text}*`;
            if (mark.type === 'link') text = `[${text}](${mark.attrs.href})`;
          });
        }
        return text;

      case 'paragraph':
        return (node.content || []).map(processNode).join('') + '\n';

      case 'bulletList':
        return (node.content || []).map(item => {
          const content = (item.content || []).map(processNode).join('').trim();
          return `- ${content}\n`;
        }).join('');

      case 'orderedList':
        return (node.content || []).map((item, i) => {
          const content = (item.content || []).map(processNode).join('').trim();
          return `${i + 1}. ${content}\n`;
        }).join('');

      case 'listItem':
        return (node.content || []).map(processNode).join('');

      default:
        if (node.content) {
          return node.content.map(processNode).join('');
        }
        return '';
    }
  };

  return json.content.map(processNode).join('').trim();
}

// Convert markdown-like text back to TipTap content
function markdownToContent(markdown) {
  if (!markdown) return null;

  const lines = markdown.split('\n');
  const content = [];
  let currentList = null;
  let listType = null;

  lines.forEach(line => {
    if (line.match(/^- /)) {
      if (listType !== 'bullet') {
        if (currentList) content.push(currentList);
        currentList = { type: 'bulletList', content: [] };
        listType = 'bullet';
      }
      currentList.content.push({
        type: 'listItem',
        content: [{ type: 'paragraph', content: parseInlineMarks(line.substring(2)) }]
      });
    } else if (line.match(/^\d+\. /)) {
      if (listType !== 'ordered') {
        if (currentList) content.push(currentList);
        currentList = { type: 'orderedList', content: [] };
        listType = 'ordered';
      }
      currentList.content.push({
        type: 'listItem',
        content: [{ type: 'paragraph', content: parseInlineMarks(line.replace(/^\d+\. /, '')) }]
      });
    } else {
      if (currentList) {
        content.push(currentList);
        currentList = null;
        listType = null;
      }
      if (line.trim()) {
        content.push({
          type: 'paragraph',
          content: parseInlineMarks(line)
        });
      }
    }
  });

  if (currentList) content.push(currentList);

  return { type: 'doc', content: content.length ? content : [{ type: 'paragraph' }] };
}

// Parse inline marks (bold, italic, links)
function parseInlineMarks(text) {
  const result = [];
  let remaining = text;

  while (remaining.length > 0) {
    // Check for markdown links [text](url)
    const linkMatch = remaining.match(/^\[([^\]]+)\]\(([^)]+)\)/);
    if (linkMatch) {
      result.push({
        type: 'text',
        marks: [{ type: 'link', attrs: { href: linkMatch[2], target: '_blank' } }],
        text: linkMatch[1]
      });
      remaining = remaining.substring(linkMatch[0].length);
      continue;
    }

    // Check for bold
    const boldMatch = remaining.match(/^\*\*([^*]+)\*\*/);
    if (boldMatch) {
      result.push({ type: 'text', marks: [{ type: 'bold' }], text: boldMatch[1] });
      remaining = remaining.substring(boldMatch[0].length);
      continue;
    }

    // Check for italic
    const italicMatch = remaining.match(/^\*([^*]+)\*/);
    if (italicMatch) {
      result.push({ type: 'text', marks: [{ type: 'italic' }], text: italicMatch[1] });
      remaining = remaining.substring(italicMatch[0].length);
      continue;
    }

    // Regular text until next special character
    const nextSpecial = remaining.search(/[\[*]/);
    if (nextSpecial === -1) {
      result.push({ type: 'text', text: remaining });
      break;
    } else if (nextSpecial === 0) {
      result.push({ type: 'text', text: remaining[0] });
      remaining = remaining.substring(1);
    } else {
      result.push({ type: 'text', text: remaining.substring(0, nextSpecial) });
      remaining = remaining.substring(nextSpecial);
    }
  }

  return result.length ? result : undefined;
}

// Toolbar button - uses onMouseDown to prevent focus loss
function ToolbarButton({ onClick, isActive, children, title }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => {
        e.preventDefault();
        onClick();
      }}
      title={title}
      className={`p-1.5 rounded transition-colors ${
        isActive
          ? 'bg-accent/20 text-accent'
          : 'text-text-tertiary hover:text-text-primary hover:bg-surface-hover'
      }`}
    >
      {children}
    </button>
  );
}

// Portal-based link picker dropdown
function LinkPicker({ type, items, onSelect, onClose, anchorRef }) {
  const [search, setSearch] = useState('');
  const [position, setPosition] = useState({ top: 0, left: 0, flip: false });
  const inputRef = useRef(null);

  useEffect(() => {
    if (anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      const dropdownHeight = 280;
      const shouldFlip = rect.bottom + dropdownHeight > window.innerHeight;

      setPosition({
        top: shouldFlip ? rect.top : rect.bottom + 4,
        left: Math.min(rect.left, window.innerWidth - 272),
        flip: shouldFlip
      });
    }
  }, [anchorRef]);

  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 10);
    return () => clearTimeout(timer);
  }, []);

  const filteredItems = items.filter(item =>
    item.name.toLowerCase().includes(search.toLowerCase())
  ).slice(0, 8);

  const Icon = type === 'doc' ? FileText : ClipboardList;
  const iconColor = type === 'doc' ? 'text-blue-400' : 'text-purple-400';

  return createPortal(
    <>
      <div className="fixed inset-0 z-[99]" onClick={onClose} />
      <div
        style={{
          position: 'fixed',
          top: position.flip ? 'auto' : position.top,
          bottom: position.flip ? (window.innerHeight - position.top + 4) : 'auto',
          left: position.left,
        }}
        className="w-64 bg-surface-card border border-border rounded-lg shadow-xl z-[100]"
      >
        <div className="p-2 border-b border-border">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search ${type === 'doc' ? 'documents' : 'policies'}...`}
              className="w-full pl-8 pr-3 py-2 text-sm bg-surface-elevated border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </div>
        </div>
        <div className="max-h-52 overflow-y-auto">
          {filteredItems.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-text-muted">
              No {type === 'doc' ? 'documents' : 'policies'} found
            </div>
          ) : (
            filteredItems.map(item => (
              <button
                key={item.id}
                onClick={() => onSelect(item)}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-surface-hover transition-colors"
              >
                <Icon size={14} className={iconColor} />
                <span className="text-sm text-text-primary truncate flex-1">{item.name}</span>
              </button>
            ))
          )}
        </div>
      </div>
    </>,
    document.body
  );
}

const RichTextEditor = forwardRef(function RichTextEditor({
  content,
  onChange,
  onLinkDocument,
  onLinkPolicy,
  documents = [],
  policies = [],
  placeholder = 'Add notes...',
  className = ''
}, ref) {
  const [showDocPicker, setShowDocPicker] = useState(false);
  const [showPolicyPicker, setShowPolicyPicker] = useState(false);
  const docButtonRef = useRef(null);
  const policyButtonRef = useRef(null);

  // Force re-render counter - used to update toolbar state immediately
  const [, forceUpdate] = useReducer(x => x + 1, 0);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
      }),
      Link.configure({
        openOnClick: true,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
          class: 'text-accent underline cursor-pointer',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: content ? markdownToContent(content) : '',
    onUpdate: ({ editor }) => {
      const json = editor.getJSON();
      const markdown = contentToMarkdown(json);
      onChange?.(markdown);
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[60px] p-2',
      },
    },
  });

  // Subscribe to editor transactions to update toolbar button states immediately
  // This is the permanent fix for delayed button highlighting
  useEffect(() => {
    if (!editor) return;

    // Force component re-render on any editor state change
    const handleUpdate = () => forceUpdate();

    editor.on('transaction', handleUpdate);
    editor.on('selectionUpdate', handleUpdate);

    return () => {
      editor.off('transaction', handleUpdate);
      editor.off('selectionUpdate', handleUpdate);
    };
  }, [editor]);

  useImperativeHandle(ref, () => ({
    focus: () => editor?.commands.focus(),
    clear: () => editor?.commands.clearContent(),
  }));

  useEffect(() => {
    if (editor && content !== undefined) {
      const currentMarkdown = contentToMarkdown(editor.getJSON());
      if (currentMarkdown !== content) {
        editor.commands.setContent(content ? markdownToContent(content) : '');
      }
    }
  }, [content, editor]);

  // Insert a clickable link into the editor
  const handleSelectDocument = useCallback((doc) => {
    if (onLinkDocument) {
      onLinkDocument(doc.id);
    }

    // Insert as a clickable link if URL exists, otherwise just reference the linked badge
    if (doc.url) {
      editor?.chain().focus().insertContent([
        {
          type: 'text',
          marks: [{ type: 'link', attrs: { href: doc.url, target: '_blank' } }],
          text: `📄 ${doc.name}`
        },
        { type: 'text', text: ' ' }
      ]).run();
    } else {
      // No URL - just focus back to editor, the badge below will show the link
      editor?.chain().focus().run();
    }

    setShowDocPicker(false);
  }, [editor, onLinkDocument]);

  const handleSelectPolicy = useCallback((policy) => {
    if (onLinkPolicy) {
      onLinkPolicy(policy.id);
    }

    // Insert as a clickable link if URL exists
    if (policy.url) {
      editor?.chain().focus().insertContent([
        {
          type: 'text',
          marks: [{ type: 'link', attrs: { href: policy.url, target: '_blank' } }],
          text: `📋 ${policy.name}`
        },
        { type: 'text', text: ' ' }
      ]).run();
    } else {
      editor?.chain().focus().run();
    }

    setShowPolicyPicker(false);
  }, [editor, onLinkPolicy]);

  if (!editor) return null;

  return (
    <div className={`border border-border rounded-lg bg-surface-elevated overflow-hidden ${className}`}>
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-border bg-surface-card">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          title="Bold"
        >
          <Bold size={14} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          title="Italic"
        >
          <Italic size={14} />
        </ToolbarButton>
        <div className="w-px h-4 bg-border mx-0.5" />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          title="Bullet List"
        >
          <List size={14} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          title="Numbered List"
        >
          <ListOrdered size={14} />
        </ToolbarButton>

        <div className="flex-1" />

        {documents.length > 0 && (
          <button
            ref={docButtonRef}
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              setShowDocPicker(!showDocPicker);
              setShowPolicyPicker(false);
            }}
            title="Link Document"
            className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors ${
              showDocPicker
                ? 'bg-blue-500/20 text-blue-400'
                : 'text-text-tertiary hover:text-blue-400 hover:bg-blue-500/10'
            }`}
          >
            <FileText size={12} />
            Doc
          </button>
        )}

        {policies.length > 0 && (
          <button
            ref={policyButtonRef}
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              setShowPolicyPicker(!showPolicyPicker);
              setShowDocPicker(false);
            }}
            title="Link Policy"
            className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors ${
              showPolicyPicker
                ? 'bg-purple-500/20 text-purple-400'
                : 'text-text-tertiary hover:text-purple-400 hover:bg-purple-500/10'
            }`}
          >
            <ClipboardList size={12} />
            Policy
          </button>
        )}
      </div>

      <EditorContent editor={editor} />

      {showDocPicker && documents.length > 0 && (
        <LinkPicker
          type="doc"
          items={documents}
          onSelect={handleSelectDocument}
          onClose={() => setShowDocPicker(false)}
          anchorRef={docButtonRef}
        />
      )}
      {showPolicyPicker && policies.length > 0 && (
        <LinkPicker
          type="policy"
          items={policies}
          onSelect={handleSelectPolicy}
          onClose={() => setShowPolicyPicker(false)}
          anchorRef={policyButtonRef}
        />
      )}

      <style>{`
        .ProseMirror {
          min-height: 60px;
          padding: 8px;
        }
        .ProseMirror p {
          margin: 0 0 0.5em 0;
        }
        .ProseMirror p:last-child {
          margin-bottom: 0;
        }
        .ProseMirror ul, .ProseMirror ol {
          padding-left: 1.5em;
          margin: 0.5em 0;
        }
        .ProseMirror li {
          margin: 0.25em 0;
        }
        .ProseMirror a {
          color: var(--color-accent, #C45D3A);
          text-decoration: underline;
          cursor: pointer;
        }
        .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: var(--color-text-muted);
          pointer-events: none;
          height: 0;
        }
      `}</style>
    </div>
  );
});

export default RichTextEditor;
